/* Form-family park: the cell editor converts with the Table round (component-map.md). */
import {
  Box,
  FormControl,
  FormLabel,
  Input,
  Popover as ChakraPopover,
  PopoverContent as ChakraPopoverContent,
  useDisclosure,
} from '@chakra-ui/react';
import { Text } from '@main/complykit/components/text';
import { Nullable } from '@main/shared/types';
import { FilterMode, isIsEmptyOrIsNotEmpty } from '@main/shared/url-helpers';
import { isEmpty } from '@main/shared/utils/is-empty';
import { CellContext, ColumnDef, Row, SortDirection } from '@tanstack/react-table';
import equal from 'fast-deep-equal';
import { ElementRef, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { z } from 'zod/v4';

import { EditablePopover } from '../../editable';
import { OverflowContainer } from '../../overflow-tooltip';
import { errorToast } from '../../toast';
import { BulkChangeConfigBase, useSyncIsBulkLoading } from '../bulk-actions';
import { createFilterHelper } from '../filters';
import { FilterLabelRow } from '../filters/filter-label-row';
import { FilterModeSelector } from '../filters/filter-mode';
import { CellContainer } from '../shared/cell';
import { CanEditGuard, getEditable, ifBulkEditable } from '../table';
import {
  bulkScalarClearAction,
  BulkScalarEditAction,
  ScalarBulkClearButton,
} from './scalar-bulk-clear';

type EditProps<TData> = {
  onChange: (row: Row<TData>, value: number | undefined) => void;
  canEditGuard?: CanEditGuard<TData, number | undefined>;
  defaultIsEditing?: (row: Row<TData>) => boolean;
};

export type BulkNumberOnChange<TData> = (
  rows: Row<TData>[],
  value: number | undefined,
  action?: BulkScalarEditAction,
) => unknown;
export type UseBulkNumberChangeHook<TData> = () => readonly [BulkNumberOnChange<TData>, boolean];

type NumberColumnConfig<TData, TFilter, TSort> = {
  header: string;
  formatValue?: (value: number | undefined, row: Row<TData>) => string;
  edit?: EditProps<TData>;
  bulkEdit?: {
    useBulkChange: UseBulkNumberChangeHook<TData>;
    allowClearValue?: boolean;
    bulkInitialValue?: number;
  } & Omit<BulkChangeConfigBase, 'onClick' | 'wrapper'>;
  getGlobalFilterCondition?(filterValue: string): Nullable<TFilter>;
  getColumnFilterCondition?(filterMode: FilterMode, filterValue: string): Nullable<TFilter>;
  getColumnSort?(sortDirection: SortDirection): TSort;
};

const filterHelper = createFilterHelper({
  filterSchema: z.string(),
  filterFn,
});

function filterFn(
  value: number | undefined,
  filterValue: string | null,
  filterMode?: FilterMode,
): boolean {
  if (isIsEmptyOrIsNotEmpty(filterMode)) {
    if (filterMode === FilterMode.IsEmpty) {
      return isEmpty(value);
    }
    if (filterMode === FilterMode.IsNotEmpty) {
      return !isEmpty(value);
    }
  }

  const filterValueAsNumber = Number(filterValue);
  if (isNaN(filterValueAsNumber)) {
    return false;
  }

  if (!filterValueAsNumber) {
    return true;
  }

  if (typeof value !== 'number') {
    return false;
  }

  if (!filterMode || filterMode === FilterMode.Includes) {
    return value === filterValueAsNumber;
  }

  if (filterMode === FilterMode.NotEquals) {
    return value !== filterValueAsNumber;
  }

  if (filterMode === FilterMode.GreaterThan) {
    return value > filterValueAsNumber;
  }

  if (filterMode === FilterMode.LessThan) {
    return value < filterValueAsNumber;
  }

  if (filterMode === FilterMode.GreaterThanOrEqual) {
    return value >= filterValueAsNumber;
  }

  if (filterMode === FilterMode.LessThanOrEqual) {
    return value <= filterValueAsNumber;
  }

  return false;
}

const anyValueChanged = <TData,>(
  rows: Row<TData>[],
  columnId: string,
  newValue: number | undefined,
): boolean => rows.some((row) => row.getValue(columnId) !== newValue);

const getBulkInitialValue = <TData,>(rows: Row<TData>[], columnId: string): number | undefined => {
  const [firstRow, ...restRows] = rows;
  if (!firstRow) return undefined;

  const value = firstRow.getValue<number | undefined>(columnId);
  return restRows.every((row) => equal(row.getValue(columnId), value)) ? value : undefined;
};

export function number<TData, TFilter, TSort>() {
  return ({
    edit,
    formatValue,
    bulkEdit,
    ...columnDef
  }: ColumnDef<TData, number | undefined> & NumberColumnConfig<TData, TFilter, TSort>): ColumnDef<
    TData,
    number | undefined
  > => {
    const useBulkChange = bulkEdit?.useBulkChange;
    const editable = getEditable(edit);
    return {
      enableGlobalFilter: false,
      enableColumnFilter: false,
      enableSorting: false,
      sortDescFirst: false,

      ...columnDef,

      cell: (context) => {
        if (edit && editable(context.row.original, context)) {
          return <EditableCell context={context} onChange={edit.onChange} />;
        }

        const value = context.getValue();
        const formattedValue = formatValue ? formatValue(value, context.row) : value?.toString();

        return (
          <CellContainer cell={context.cell} p={0}>
            <OverflowContainer>
              <OverflowContainer.Tooltip
                label={value}
                hasArrow
                placement="bottom-start"
                fontSize="sm"
                openDelay={500}
              >
                {columnDef.cell && typeof columnDef.cell === 'function' ? (
                  columnDef.cell(context)
                ) : (
                  <Box py={4} px={2}>
                    <Text size="inherit" isTruncated>
                      {formattedValue}
                    </Text>
                  </Box>
                )}
              </OverflowContainer.Tooltip>
            </OverflowContainer>
          </CellContainer>
        );
      },

      filterFn: filterHelper.filterFn,

      meta: {
        ...columnDef.meta,
        columnType: 'number',
        name: columnDef.header,
        globalFilterFn: filterFn,
        editable,
        bulkEdit: ifBulkEditable(edit, bulkEdit, () => {
          if (!bulkEdit || !useBulkChange) {
            throw new Error('Bulk edit config requires a bulk change handler');
          }

          return {
            ...bulkEdit,
            id: columnDef.id ?? '',
            editor: (editorRows, trigger, isInMenu, onOpenChange, labelledBy) => (
              <BulkNumberEditor
                id={columnDef.id ?? ''}
                key={columnDef.id}
                trigger={trigger}
                rows={editorRows}
                useBulkChange={useBulkChange}
                allowClearValue={bulkEdit.allowClearValue}
                defaultValue={
                  bulkEdit.bulkInitialValue ?? getBulkInitialValue(editorRows, columnDef.id ?? '')
                }
                isInMenu={isInMenu}
                onOpenChange={onOpenChange}
                labelledBy={labelledBy}
              />
            ),
          };
        }),
        // Tanstack table filters are untyped. Type safety is enforced via filter/column helpers
        getGlobalFilterCondition: columnDef.getGlobalFilterCondition as never,
        getColumnFilterCondition: columnDef.getColumnFilterCondition as never,
        getColumnSort: columnDef.getColumnSort as never,

        filter: {
          filterSchema: filterHelper.filterSchema,

          DisplayFilter: ({ column }) => {
            const { t } = useTranslation('ui');
            const mode = filterHelper.getFilterMode(column);
            const filterValue = filterHelper.getFilterValue(column);

            return (
              <span>
                {mode === FilterMode.Includes && `${t('table.filter.equals')} `}
                {mode === FilterMode.NotEquals && `${t('table.filter.notEquals')} `}
                {mode === FilterMode.GreaterThan && `${t('table.filter.greaterThan')} `}
                {mode === FilterMode.LessThan && `${t('table.filter.lessThan')} `}
                {mode === FilterMode.GreaterThanOrEqual &&
                  `${t('table.filter.greaterThanOrEqual')} `}
                {mode === FilterMode.LessThanOrEqual && `${t('table.filter.lessThanOrEqual')} `}
                {mode === FilterMode.IsEmpty && t('table.filter.isEmpty')}
                {mode === FilterMode.IsNotEmpty && t('table.filter.isNotEmpty')}
                {filterValue}
              </span>
            );
          },

          EditFilter: ({ column }) => {
            const { t } = useTranslation('ui');

            const filterValue = filterHelper.getFilterValue(column);
            const mode = filterHelper.getFilterMode(column);
            const modeOptions = useMemo(
              () => [
                { label: t('table.filter.equals'), value: FilterMode.Includes },
                { label: t('table.filter.notEquals'), value: FilterMode.NotEquals },
                { label: t('table.filter.greaterThan'), value: FilterMode.GreaterThan },
                { label: t('table.filter.lessThan'), value: FilterMode.LessThan },
                {
                  label: t('table.filter.greaterThanOrEqual'),
                  value: FilterMode.GreaterThanOrEqual,
                },
                { label: t('table.filter.lessThanOrEqual'), value: FilterMode.LessThanOrEqual },
                { label: t('table.filter.isEmpty'), value: FilterMode.IsEmpty },
                { label: t('table.filter.isNotEmpty'), value: FilterMode.IsNotEmpty },
              ],
              [t],
            );

            const label = useMemo(
              () => modeOptions.find((option) => option.value === mode)?.label ?? '',
              [mode, modeOptions],
            );
            const hasInput = !isIsEmptyOrIsNotEmpty(mode);
            return (
              <FormControl p={3}>
                <FormLabel fontWeight="medium" mb={hasInput ? undefined : 0}>
                  <FilterLabelRow label={columnDef.header}>
                    <FilterModeSelector {...filterHelper} column={column} options={modeOptions} />
                  </FilterLabelRow>
                </FormLabel>
                {!isIsEmptyOrIsNotEmpty(mode) && (
                  <Input
                    autoFocus
                    size="sm"
                    type="text"
                    inputMode="decimal"
                    pattern="[0-9]*(\\.[0-9]+)?"
                    placeholder={t('table.column.text.filterPlacehoder')}
                    borderRadius="base"
                    value={filterValue ?? ''}
                    aria-label={`${columnDef.header} ${label}`}
                    onChange={(event) =>
                      filterHelper.setFilterValue(column, event.target.value || null)
                    }
                    onKeyDown={(event) => {
                      if (!isValidNumericKeyboardEvent(event, isFloatingPointNumericCharacter)) {
                        event.preventDefault();
                      }
                    }}
                  />
                )}
              </FormControl>
            );
          },
        },
      },
    };
  };
}

function EditableCell<TData>({
  context,
  onChange,
}: {
  context: CellContext<TData, number | undefined>;
  onChange: (row: Row<TData>, value: number | undefined) => void;
}) {
  const { isOpen, onToggle, onClose } = useDisclosure();
  const cellValue = context.getValue();
  const value = cellValue == null ? undefined : cellValue;
  const displayValue = value == null ? '' : value.toString();
  const initialFocusRef = useRef<ElementRef<'input'>>(null);

  const onCellUpdate = () => {
    const newValue = initialFocusRef.current?.value ?? '';

    if (newValue === '') {
      onChange(context.row, undefined);
      onClose();
      return;
    }

    const newValueNumber = Number(newValue);
    if (newValueNumber !== value && !Number.isNaN(newValueNumber)) {
      onChange(context.row, newValueNumber);
      onClose();
      return;
    }

    onClose();
  };

  return (
    <>
      <OverflowContainer
        data-stop-row-click="true"
        justify="space-between"
        onClick={(event) => {
          event.stopPropagation();
          onToggle();
        }}
      >
        <OverflowContainer.Tooltip
          label={displayValue}
          placement="bottom-start"
          fontSize="sm"
          openDelay={500}
        >
          <Box p={4} w="full">
            <Text size="inherit" isTruncated>
              {displayValue}
            </Text>
          </Box>
        </OverflowContainer.Tooltip>
      </OverflowContainer>
      {/* Parked with the Table round (popover.md): a cell's editor is the table's contract. */}
      <ChakraPopover
        isLazy
        initialFocusRef={initialFocusRef}
        isOpen={isOpen}
        onClose={onCellUpdate}
      >
        <ChakraPopoverContent
          maxW="fit-content"
          _focusVisible={{
            outline: 'none',
          }}
          width={context.column.columnDef.size}
          onClick={(event) => {
            event.stopPropagation();
          }}
        >
          <Input
            inputMode="decimal"
            pattern={NUMBER_PATTERN}
            ref={initialFocusRef}
            defaultValue={displayValue}
            h="50px"
            rounded={0}
            fontSize="sm"
            focusBorderColor="gray.200"
            px="14px"
            onKeyDown={(event) => {
              if (!isValidNumericKeyboardEvent(event, isFloatingPointNumericCharacter)) {
                event.preventDefault();
                return;
              }

              if (event.key === 'Enter') {
                event.preventDefault();
                onCellUpdate();
              }
            }}
          />
        </ChakraPopoverContent>
      </ChakraPopover>
    </>
  );
}

type BulkNumberEditorProps<TData> = {
  id: string;
  defaultValue?: number;
  trigger: JSX.Element;
  rows: Row<TData>[];
  useBulkChange: UseBulkNumberChangeHook<TData>;
  allowClearValue?: boolean;
  isInMenu?: boolean;
  onOpenChange?: (isOpen: boolean) => void;
  labelledBy?: string;
};

function BulkNumberEditor<TData>({
  id,
  defaultValue,
  trigger,
  rows,
  useBulkChange,
  allowClearValue = false,
  isInMenu,
  onOpenChange,
  labelledBy,
}: BulkNumberEditorProps<TData>) {
  const { t } = useTranslation('ui');
  const inputRef = useRef<ElementRef<'input'>>(null);
  const skipNextUpdateRef = useRef(false);
  const defaultInputValue = defaultValue?.toString() ?? '';
  const [onChange, isLoading] = useBulkChange();

  useSyncIsBulkLoading(id, isLoading);

  const onUpdate = async () => {
    if (isLoading) {
      return;
    }

    if (skipNextUpdateRef.current) {
      skipNextUpdateRef.current = false;
      return;
    }

    const nextValue = inputRef.current?.value.trim() ?? '';

    if (nextValue === '') {
      if (!allowClearValue) {
        errorToast(t('table.invalidValue'));
      }
      return;
    }

    if (nextValue === defaultInputValue) {
      return;
    }

    if (!(inputRef.current?.validity.valid ?? true)) {
      errorToast(t('table.invalidValue'));
      return;
    }

    const parsedValue = Number(nextValue);
    if (Number.isNaN(parsedValue)) {
      errorToast(t('table.invalidValue'));
      return;
    }

    if (!anyValueChanged(rows, id, parsedValue)) {
      return;
    }

    await onChange(rows, parsedValue);
  };

  const handleOpenChange = (isOpen: boolean) => {
    if (isOpen) {
      skipNextUpdateRef.current = false;
    }
    onOpenChange?.(isOpen);
  };

  const handleClear = async (setOpen: (isOpen: boolean) => void) => {
    if (isLoading) {
      return;
    }

    const previousValue = inputRef.current?.value;
    skipNextUpdateRef.current = true;
    if (inputRef.current) {
      inputRef.current.value = '';
    }

    const didUpdate = await onChange(rows, undefined, bulkScalarClearAction);
    if (didUpdate !== false) {
      skipNextUpdateRef.current = true;
      setOpen(false);
      return;
    }

    skipNextUpdateRef.current = false;
    if (inputRef.current && previousValue !== undefined) {
      inputRef.current.value = previousValue;
    }
  };

  return (
    <EditablePopover
      onClose={onUpdate}
      onOpenChange={handleOpenChange}
      closeOnBlur={false}
      openEvent={isInMenu ? 'mousedown' : undefined}
      placement={isInMenu ? 'left-start' : 'top-end'}
    >
      <EditablePopover.Trigger>{trigger}</EditablePopover.Trigger>
      <EditablePopover.Content
        bgColor="gray.25"
        _dark={{ bgColor: 'gray.700' }}
        offset={-1}
        data-testid={`bulk-editor-${id}`}
      >
        {(context) => {
          const input = (
            <Input
              ref={inputRef}
              autoFocus
              size="sm"
              type="text"
              variant="unstyled"
              py={4}
              pl={4}
              pr={allowClearValue ? 10 : 4}
              maxH="50px"
              outline="2px solid"
              outlineColor="chakra-border-color"
              defaultValue={defaultInputValue}
              onBlur={() => context.setOpen(false)}
              placeholder={t('table.column.text.filterPlacehoder')}
              aria-labelledby={labelledBy}
              inputMode="decimal"
              pattern={NUMBER_PATTERN}
              onKeyDown={(event) => {
                if (!isValidNumericKeyboardEvent(event, isFloatingPointNumericCharacter)) {
                  event.preventDefault();
                  return;
                }

                if (event.key === 'Enter') {
                  event.preventDefault();
                  event.stopPropagation();
                  const nextValue = event.currentTarget.value.trim();

                  if (nextValue !== '' && !event.currentTarget.validity.valid) {
                    errorToast(t('table.invalidValue'));
                    return;
                  }

                  context.setOpen(false);
                }
              }}
            />
          );

          return allowClearValue ? (
            <Box position="relative">
              {input}
              <ScalarBulkClearButton
                isDisabled={isLoading}
                onClick={() => void handleClear(context.setOpen)}
              />
            </Box>
          ) : (
            input
          );
        }}
      </EditablePopover.Content>
    </EditablePopover>
  );
}

export const NUMBER_PATTERN = '[0-9]*(.[0-9]+)?';

const FLOATING_POINT_REGEX = /^[Ee0-9+\-.]$/;

export function isFloatingPointNumericCharacter(character: string) {
  return FLOATING_POINT_REGEX.test(character);
}

export function isValidNumericKeyboardEvent(
  event: React.KeyboardEvent,
  isValid: (key: string) => boolean,
) {
  if (event.key == null) return true;
  const isModifierKey = event.ctrlKey || event.altKey || event.metaKey;
  const isSingleCharacterKey = event.key.length === 1;
  if (!isSingleCharacterKey || isModifierKey) return true;
  return isValid(event.key);
}
